import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Camera, Loader2, MapPin, Mail, Calendar } from 'lucide-react';
import { formatDate } from '../lib/utils';

export default function ProfilePage() {
  const { user, updateProfile } = useAuth();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    location: user?.location || ''
  });
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large. Max 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePhoto(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.location) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await updateProfile({
        name: formData.name,
        location: formData.location,
        profile_photo: profilePhoto
      });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F7] animate-fadeIn" data-testid="profile-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Profile</h1>
          <p className="text-stone-600">Manage your account information</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Profile Photo */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-28 h-28 rounded-full overflow-hidden bg-stone-100">
                  {profilePhoto ? (
                    <img 
                      src={profilePhoto} 
                      alt={formData.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-[#008080] text-white flex items-center justify-center text-4xl font-medium">
                      {formData.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-[#008080] text-white flex items-center justify-center hover:bg-[#008080]/90 transition-colors"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="photo-input"
                />
              </div>
              <p className="text-sm text-stone-500 mt-3">Click to change photo</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-5">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1.5 h-12 rounded-xl"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <Label htmlFor="location">City</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="mt-1.5 h-12 rounded-xl"
                  data-testid="profile-location-input"
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="mt-1.5 h-12 rounded-xl bg-stone-50"
                />
                <p className="text-xs text-stone-500 mt-1">Email cannot be changed</p>
              </div>
            </div>

            {/* Account Info */}
            <div className="pt-6 border-t border-stone-100">
              <h3 className="text-sm font-medium text-stone-700 mb-4">Account Information</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-stone-600">
                  <Mail className="w-4 h-4 mr-2 text-stone-400" />
                  {user?.email}
                </div>
                <div className="flex items-center text-stone-600">
                  <MapPin className="w-4 h-4 mr-2 text-stone-400" />
                  {user?.location}
                </div>
                <div className="flex items-center text-stone-600">
                  <Calendar className="w-4 h-4 mr-2 text-stone-400" />
                  Joined {user?.created_at ? formatDate(user.created_at) : 'Unknown'}
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#008080] hover:bg-[#008080]/90 rounded-full"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
